/* oxlint-disable max-statements */
/* oxlint-disable max-lines-per-function */
import React, { useCallback, useMemo, useRef } from 'react';

import { Separator, Spinner } from '@change/ds-core-ui';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import {
	usePetitionShareCounts,
	useGenPetitionUrl,
	useScrollToSection,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { useMobileNativeShareAvailable } from '#src/app/shared/hooks/share';
import { shareNative } from '#src/app/shared/share/mobileNativeShare';
import { focus } from '#src/app/shared/utils/dom';

import { useSpotlightCardContent } from '../../hooks/useSpotlightCardContent';
import { useMomentumBarTracking } from '../../hooks/useTracking';
import { filterVideoComments, getFirstVideoThumbnail, hasVideoComments } from '../../utils';
import { CounterUpdate } from '../CounterUpdate';
import { MediaOutletLogos } from '../MediaOutletLogos';
import { SignersCarousel } from '../SignersCarousel';
import { SpotlightCard } from '../SpotlightCard';
import { VideoPreviewButton } from '../VideoPreviewButton';

type Props = Readonly<{
	shouldFetchShareCounts?: boolean;
}>;

/* oxlint-disable-next-line complexity */
const MomentumBarComponent: React.FC<Props> = ({ shouldFetchShareCounts }) => {
	const {
		data: {
			petition: { id: petitionId, ask, supporterCommentsConnection, recentPublicSignersConnectionV2 },
			supporterVoicesVideos,
			mediaHitsPage,
			mediaSourcesPage,
		},
	} = usePetitionDetailsPageData();
	const { translate } = useI18n();
	const tracking = useMomentumBarTracking();
	const utilityContext = useUtilityContext();
	const genUrl = useGenPetitionUrl();
	const isNativeShareAvailable = useMobileNativeShareAvailable();
	const spotlightItems = useSpotlightCardContent();
	const {
		totalShareCount,
		totalShareCountLocalized,
		isLoading: isShareCountsLoading,
	} = usePetitionShareCounts({
		enabled: shouldFetchShareCounts !== false,
	});
	const hasTrackedPreviewView = useRef(false);

	const handleSharesClick = useCallback(() => {
		tracking.trackSharesClick(petitionId);
		const url = utilityContext.resolveUrl(genUrl({ medium: 'momentum_bar_shares' }));
		void shareNative(
			{ title: ask, url },
			{
				trackingEvents: {
					success: 'momentum_bar_shares_share_success',
					failure: 'momentum_bar_shares_share_failure',
				},
				trackingData: { petition_id: petitionId },
			},
			tracking.track,
			utilityContext,
		);
	}, [ask, petitionId, genUrl, tracking, utilityContext]);

	const handleVideoPreviewAccess = useCallback(() => {
		if (hasTrackedPreviewView.current) return;
		hasTrackedPreviewView.current = true;
		tracking.trackVideoPreviewView(petitionId);
	}, [petitionId, tracking]);

	const handleVideoPreviewBlur = useCallback(() => {
		hasTrackedPreviewView.current = false;
	}, []);

	const {
		actions: { scrollToSection },
	} = useScrollToSection();
	const handleVideoPreviewClick = useCallback(() => {
		scrollToSection(SECTION_ID_MAPPING.SUPPORTER_VOICES);
		focus(document.querySelector<HTMLElement>(`#${SECTION_ID_MAPPING.SUPPORTER_VOICES}`), {
			makeFocusable: true,
			preventScroll: true,
		});
	}, [scrollToSection]);

	const handleMediaLogosClick = useCallback(() => {
		tracking.trackMediaClick(petitionId);
		scrollToSection(SECTION_ID_MAPPING.MEDIA_MENTIONS);
		focus(document.querySelector<HTMLElement>(`#${SECTION_ID_MAPPING.MEDIA_MENTIONS}`), {
			makeFocusable: true,
			preventScroll: true,
		});
	}, [petitionId, scrollToSection, tracking]);

	const commentsCount = supporterCommentsConnection.totalCount;
	const commentsCountLocalized = supporterCommentsConnection.totalCountLocalized;
	const sharesCount = totalShareCount ?? 0;
	const recentSigners = recentPublicSignersConnectionV2.edges;

	const { mediaSources, numberOfMediaSources } = mediaSourcesPage;
	// The displayed count is every media hit on the petition, but the slot stays gated on media sources: they are what
	// supply the outlet logos, so hits with no identified source would render a counter with no logos. Sources are
	// derived from hits, so whenever the slot shows, the count is at least 1.
	const { numberOfMediaHitsLocalized } = mediaHitsPage;
	const hasMediaMentions = numberOfMediaSources > 0;

	const { videos } = supporterVoicesVideos;
	const hasVideos = hasVideoComments(videos);
	const firstVideoThumbnail = useMemo(() => getFirstVideoThumbnail(videos, { width: 280, height: 420 }), [videos]);
	const firstVideoThumbnailAlt = useMemo(() => {
		// .at(0) (not [0]) so the type stays `T | undefined` when the filtered list is empty —
		// bracket indexing doesn't add `| undefined` here (noUncheckedIndexedAccess is off).
		const firstVideo = filterVideoComments(videos).at(0);

		// oxlint-disable-next-line typescript/no-unnecessary-condition -- a video comment can genuinely have no attributed user (see tests), even though the field isn't typed as optional
		const name = firstVideo?.user?.shortDisplayName;
		return name
			? translate('corgi.petition_details.momentum_bar.video_thumbnail_alt_with_name', { name })
			: translate('corgi.petition_details.momentum_bar.video_thumbnail_alt');
	}, [videos, translate]);

	const videoPreview = useMemo(
		() =>
			hasVideos && firstVideoThumbnail ? (
				<VideoPreviewButton
					thumbnailAlt={firstVideoThumbnailAlt}
					thumbnailUrl={firstVideoThumbnail}
					ariaLabel={translate('corgi.petition_details.momentum_bar.video_preview_aria_label')}
					onAccess={handleVideoPreviewAccess}
					onBlur={handleVideoPreviewBlur}
					onClick={handleVideoPreviewClick}
					testIdPrefix="momentum-bar"
				/>
			) : null,
		[
			hasVideos,
			firstVideoThumbnail,
			firstVideoThumbnailAlt,
			translate,
			handleVideoPreviewAccess,
			handleVideoPreviewBlur,
			handleVideoPreviewClick,
		],
	);

	// Show a loader without the bordered container until share counts have loaded.
	if (isShareCountsLoading) {
		return (
			<div className="mb-8 flex min-h-[120px] w-full items-center justify-center" data-qa="momentum-bar-loading">
				<Spinner className="size-6" />
			</div>
		);
	}

	// Require at least comments, plus either shares or media mentions. Hide bar if no shares and no media mentions.
	if (commentsCount < 1 || (sharesCount === 0 && !hasMediaMentions)) {
		return null;
	}

	return (
		<div
			className="border-stroke-weaker mb-8 flex flex-col items-start self-stretch rounded-lg border p-3 md:p-0 md:pt-4 md:pr-3 md:pb-0 md:pl-4"
			data-qa="momentum-bar-container"
		>
			<div className="flex w-full flex-col items-start gap-0.5 p-0 lg:flex-row">
				<SpotlightCard
					items={spotlightItems}
					animatedCardsCarousel
					testId="momentum-bar-spotlight"
					qaId="momentum-bar-spotlight"
				/>
				<div
					className="flex w-full flex-row items-start gap-2.5 lg:w-[40%] lg:gap-0"
					data-testid="momentum-bar-submodules"
					data-qa="momentum-bar-submodules"
				>
					{!hasMediaMentions && sharesCount > 0 && (
						<div className="min-w-0 flex-1">
							<CounterUpdate
								label={translate('corgi.petition_gamma.momentum_bar.shares.label')}
								count={totalShareCountLocalized ?? ''}
								testId="momentum-bar-shares"
								qaId="momentum-bar-shares"
								onClick={isNativeShareAvailable ? handleSharesClick : undefined}
								stackLabelOnMobile
							/>
						</div>
					)}
					{hasMediaMentions && (
						<div className="min-w-0 flex-1">
							<CounterUpdate
								label={translate('corgi.petition_gamma.momentum_bar.media_mentions.label')}
								count={numberOfMediaHitsLocalized}
								testId="momentum-bar-media-mentions"
								qaId="momentum-bar-media-mentions"
								sectionId={SECTION_ID_MAPPING.MEDIA_MENTIONS}
								onTrackClick={() => tracking.trackMediaClick(petitionId)}
								stackLabelOnMobile
							>
								<MediaOutletLogos
									mediaSources={mediaSources}
									testIdPrefix="momentum-bar"
									onLogoClick={handleMediaLogosClick}
								/>
							</CounterUpdate>
						</div>
					)}
					<div className="min-w-0 flex-1">
						<CounterUpdate
							label={translate('corgi.petition_gamma.momentum_bar.comments.label')}
							count={commentsCountLocalized}
							testId="momentum-bar-comments"
							qaId="momentum-bar-comments"
							sectionId={SECTION_ID_MAPPING.SUPPORTER_VOICES}
							onTrackClick={() => tracking.trackCommentsClick(petitionId)}
							stackLabelOnMobile
						>
							{videoPreview}
						</CounterUpdate>
					</div>
				</div>
			</div>
			<Separator className="my-2.5 lg:hidden" />
			<div className="w-full max-w-full min-w-0 overflow-hidden motion-reduce:hidden">
				<SignersCarousel
					signers={recentSigners}
					testId="momentum-bar-signers-row"
					qaId="momentum-bar-signers-row"
					anonymousLabel={translate('corgi.petition_details.momentum_bar.recent_signers_anonymous')}
				/>
			</div>
		</div>
	);
};

export const MomentumBar = withI18nPlaceholders(I18N_PLACEHOLDERS, MomentumBarComponent);
